import {FeetInches, transportCategory, TransportEntity} from "@ots-frontend/ots-data";
import {ActionIcon, Divider, Grid, Group, Table, Text, Title, useMantineTheme} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {FormattedQuantity} from "../../text/FormattedQuantity/FormattedQuantity";
import {
  IconBoxOff,
  IconCurrencyDollarOff,
  IconDownload,
  IconFilePlus,
  IconMessagePlus,
  IconRadarOff
} from "@tabler/icons-react";
import {useEntityCommentModal} from "../../comment/EntityCommentModal/useEntityCommentModal";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {CommentModal} from "../../comment/CommentModal/CommentModal";
import {EntityCommentList} from "../../comment/EntityCommentList/EntityCommentList";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {EntityImageCarousel} from "../../carousel/EntityImageCarousel/EntityImageCarousel";
import {EntityDocumentTable} from "../../table/DocumentTable/EntityDocumentTable/EntityDocumentTable";
import {Empty} from "../../empty/Empty/Empty";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider"
import {Card} from "../../card/Card/Card";
import {SupplementalFormModal} from "../../form/SupplementalForm/SupplementalForm";
import {
  useEntitySupplementalUploadModal
} from "../../upload/EntitySupplementalUploadModal/useEntitySupplementalUploadModal";
import {useDownloadObjectPictures} from "../../../hooks/useDownloadObjectPictures";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";

export type TransportDetailsProps = {
  transport: TransportEntity
}

export const TransportDetails = (props: TransportDetailsProps) => {
  const {transport} = props;

  const {t} = useTranslation(["common"]);
  const appTheme = useMantineTheme()

  const {baseQueryApi} = useApp()
  const {permissions} = useAuth()

  const {
    opened,
    handlers,
    onSubmit: onCommentSubmit,
    isCreating: isCommentCreating
  } = useEntityCommentModal({
    entity: transport
  });

  const {
    opened: supplementalOpened,
    handlers: supplementalHandlers,
    onSubmit: onSupplementalSubmit,
    isCreating: isSupplementalCreating
  } = useEntitySupplementalUploadModal({
    entity: transport
  });

  const {
    downloadObjectPictures,
  } = useDownloadObjectPictures({baseQueryApi})

  return (
    <Grid>
      <Grid.Col span={6}>
        <Text ta="left" fz="sm" weight={500} mt="md">
          {t("profile.transport.info.field.date.label", "Date")}
        </Text>
        <Text ta="left" c="dimmed" fz="xs">
          <FormattedDateTime date={transport.createDttm}/>
        </Text>
      </Grid.Col>
      {transport.disableDemurrage &&
        <Grid.Col span={6}>
          <Text ta="left" fz="sm" weight={500} mt="md">
            {t("profile.transport.info.field.demurrage.label", "Demurrage")}
          </Text>
          <Text ta="left" c="service-work" fz="xs">
            <Group spacing="xs">
              <IconCurrencyDollarOff size="14"/>
              {t("profile.transport.info.field.demurrage.disabled", "Disabled")}
            </Group>
          </Text>
        </Grid.Col>
      }
      {transport.skipGeofenceEvent &&
        <Grid.Col span={6}>
          <Text ta="left" fz="sm" weight={500} mt="md">
            {t("profile.transport.info.field.skipegeofenceevent.label", "Skip Geofence Event")}
          </Text>
          <Text ta="left" c="service-work" fz="xs">
            <Group spacing="xs">
              <IconRadarOff size="14"/>
              {t("profile.transport.info.field.skipegeofenceevent.enabled", "Skipped")}
            </Group>
          </Text>
        </Grid.Col>
      }
      {transport.rejectionType &&
        <>
          <Grid.Col span={12}>
            <Text ta="left" fz="sm" weight={700} color="red" mt="md">
              {t("profile.transport.info.field.rejection.label", "Rejected")}
            </Text>
            <Text ta="left" c="dimmed" fz="xs">
              {t(transport.rejectionType.displayValue.id, transport.rejectionType.displayValue.defaultMessage)}
            </Text>
            {transport.rejectionComment &&
              <Text ta="left" c="dimmed" fz="xs">
                {transport.rejectionComment}
              </Text>
            }
          </Grid.Col>
        </>
      }
      <Grid.Col span={12}>
        <Divider variant="dashed"/>
      </Grid.Col>
      <Grid.Col span={12}>
        <Text ta="left" fz="sm" weight={500} mt="md">
          {t("profile.transport.info.field.trailercompany.label", "Hauler")}
        </Text>
        <Text ta="left" c="dimmed" fz="xs">
          <DefaultText value={transport.trailerCompanyName}/>
        </Text>
      </Grid.Col>
      <Grid.Col span={12}>
        <Text ta="left" fz="sm" weight={500} mt="md">
          {t("profile.transport.info.field.driver.label", "Driver")}
        </Text>
        <Text ta="left" c="dimmed" fz="xs">
          {transport.personId && (
            <EntityAnchor
              value={{
                objectId: transport.personId,
                objectName: transport.personName,
                objectType: 'PERSON'
              }}/>
          )}
        </Text>
      </Grid.Col>
      <Grid.Col span={6}>
        <Text ta="left" fz="sm" weight={500} mt="md">
          {t("profile.transport.info.field.vehicle.label", "Truck")}
        </Text>
        <Text ta="left" c="dimmed" fz="xs">
          {transport.vehicleId && (
            <EntityAnchor
              value={{
                objectId: transport.vehicleId,
                objectName: transport.vehicleName,
                objectType: 'VEHICLE'
              }}/>
          )}
        </Text>
      </Grid.Col>
      <Grid.Col span={6}>
        <Text ta="left" fz="sm" weight={500} mt="md">
          {t("profile.transport.info.field.trailer.label", "Trailer")}
        </Text>
        <Text ta="left" c="dimmed" fz="xs">
          {transport.trailerId && (
            <EntityAnchor
              value={{
                objectId: transport.trailerId,
                objectName: transport.trailerName,
                objectType: 'TRAILER'
              }}/>
          )}
        </Text>
      </Grid.Col>
      <Grid.Col span={12}>
        <Text ta="left" fz="sm" weight={500} mt="md">
          {t("profile.transport.info.field.site.label", "Site")}
        </Text>
        <Text ta="left" c="dimmed" fz="xs">
          <EntityAnchor
            value={{
              objectId: transport.siteId,
              objectName: transport.siteName,
              objectType: 'SITE'
            }}
          />
        </Text>
      </Grid.Col>
      <Grid.Col span={12}>
        <Text ta="left" fz="sm" weight={500} mt="md">
          {t("profile.transport.info.field.well.label", "Well")}
        </Text>
        <Text ta="left" c="dimmed" fz="xs">
          {transport.wellId &&
            <EntityAnchor
              value={{
                objectId: transport.wellId,
                objectName: transport.wellName,
                objectType: 'WELL'
              }}
            />
          }
        </Text>
      </Grid.Col>
      <Grid.Col span={12}>
        <Text ta="left" fz="sm" weight={500} mt="md">
          {t("profile.transport.info.field.Container.label", "Container")}
        </Text>
        <Text ta="left" c="dimmed" fz="xs">
          <EntityAnchor
            value={{
              objectId: transport.containerId,
              objectName: transport.containerName,
              objectType: 'CONTAINER'
            }}
          />
        </Text>
      </Grid.Col>
      {(transport.category === transportCategory.water || transport.serviceTypeId) &&
        <Grid.Col span={12}>
          <Text ta="left" fz="sm" weight={500} mt="md">
            {t("profile.transport.info.field.servicetype.label", "Work Type")}
          </Text>
          <Text ta="left" c="dimmed" fz="xs">
            {transport.serviceTypeId ?
              <DefaultText value={transport.serviceTypeName}/> : t("common.shared.normal", "Normal")}
          </Text>
        </Grid.Col>
      }
      <Grid.Col span={6}>
        <Text ta="left" fz="sm" weight={500} mt="md">
          {t("profile.transport.info.field.quantity.label", "Quantity")}
        </Text>
        <Text ta="left" c="dimmed" fz="xs">
          <FormattedQuantity
            value={{
              number: transport.quantityDecimal,
              units: transport.quantityUnitOfMeasure
            }}
          />
        </Text>
      </Grid.Col>
      <Grid.Col span={6}>
        <Text ta="left" fz="sm" weight={500} mt="md">
          {t("profile.transport.info.field.freighttype.label", "Freight Type")}
        </Text>
        <Text ta="left" c="dimmed" fz="xs">
          <DefaultText value={transport.waterTypeName}/>
        </Text>
      </Grid.Col>
      {transport.category === transportCategory.oil &&
        <>
          <Grid.Col span={6}>
            <Text ta="left" fz="sm" weight={500} mt="md">
              {t("profile.transport.info.field.netquantity.label", "Net Quantity")}
            </Text>
            <Text ta="left" c="dimmed" fz="xs">
              <FormattedQuantity
                value={{
                  number: transport.netQuantity,
                  units: transport.quantityUnitOfMeasure
                }}
              />
            </Text>
          </Grid.Col>
          <Grid.Col span={6}>
            <Text ta="left" fz="sm" weight={500} mt="md">
              {t("profile.transport.info.field.observedtemperature.label", "Obs. Temp.")}
            </Text>
            <Text ta="left" c="dimmed" fz="xs">
              <FormattedQuantity
                value={{
                  number: transport.observedTemperature,
                  units: transport.temperatureUom
                }}
              />
            </Text>
          </Grid.Col>
          <Grid.Col span={6}>
            <Text ta="left" fz="sm" weight={500} mt="md">
              {t("profile.transport.info.field.observedgravity.label", "Obs. Gravity")}
            </Text>
            <Text ta="left" c="dimmed" fz="xs">
              <FormattedQuantity
                value={{
                  number: transport.observedGravity,
                }}
              />
            </Text>
          </Grid.Col>
          <Grid.Col span={6}>
            <Text ta="left" fz="sm" weight={500} mt="md">
              {t("profile.transport.info.field.bsw.label", "BS&W %")}
            </Text>
            <Text ta="left" c="dimmed" fz="xs">
              <FormattedQuantity
                value={{
                  number: transport.bswPercentage,
                  units: "%"
                }}
              />
            </Text>
          </Grid.Col>
          <Grid.Col span={12}>
            <Text ta="left" fz="sm" weight={500} mt="md">
              {t("profile.transport.info.field.correctedgravity.label", "Corrected Gravity")}
            </Text>
            <Text ta="left" c="dimmed" fz="xs">
              <FormattedQuantity
                value={{
                  number: transport.correctedGravity
                }}
              />
            </Text>
          </Grid.Col>
        </>
      }
      <Grid.Col span={6}>
        <Text ta="left" fz="sm" weight={500} mt="md">
          {t("profile.transport.info.field.ticketnumber.label", "Paper Ticket #")}
        </Text>
        <Text ta="left" c="dimmed" fz="xs">
          {transport.ticketNumber}
        </Text>
      </Grid.Col>
      <Grid.Col span={6}>
        <Text ta="left" fz="sm" weight={500} mt="md">
          {t("profile.transport.info.field.regulatorynumber.label", "Regulatory #")}
        </Text>
        <Text ta="left" c="dimmed" fz="xs">
          {transport.regulatoryNumber}
        </Text>
      </Grid.Col>
      <Grid.Col span={6}>
        <Text ta="left" fz="sm" weight={500} mt="md">
          {t("profile.transport.info.field.ticketnumber.label", "Work Order #")}
        </Text>
        <Text ta="left" c="dimmed" fz="xs">
          {transport.workOrderNumber}
        </Text>
      </Grid.Col>

      <Grid.Col span={12}>
        <Divider variant="dashed"/>
      </Grid.Col>

      <Grid.Col span={12}>
        <Group position="apart" m="xl">
          <Title order={4}>
            {t("common.entity.dispatch.profile.info.section.gauges.label", "Gauges")}
          </Title>
        </Group>
        <Divider/>
        <Grid>
          {(transport.gauges && transport.gauges.length > 0) ? (
            <Grid.Col span={12}>
              <Card shadow="sm" padding="sm" mb="sm" radius="sm">
                <Table>
                  <thead>
                  <tr>
                    <th>
                      Container
                    </th>
                    <th>
                      Type
                    </th>
                    <th>
                      Top
                    </th>
                    <th>
                      Bottom
                    </th>
                  </tr>
                  </thead>
                  <tbody>
                  {transport.gauges.map((g, index) => {
                    return (
                      <tr key={index}>
                        <td>
                          <DefaultText
                            value={g.containerName}
                            defaultValue={"Ticket Container"}/>
                        </td>
                        <td>
                          {t(g.type.displayValue.id, g.type.displayValue.defaultMessage)}
                        </td>
                        <td>
                          {FeetInches.fromInches(g.topValue).toString()}
                        </td>
                        <td>
                          {FeetInches.fromInches(g.bottomValue).toString()}
                        </td>
                      </tr>
                    )
                  })}
                  </tbody>
                </Table>
              </Card>
            </Grid.Col>
          ) : (
            <Empty
              maw={400}
              h={100}
              mx="auto"
              message={"No Gauges"}/>
          )
          }
        </Grid>
      </Grid.Col>

      <Grid.Col span={12}>
        <Divider variant="dashed"/>
      </Grid.Col>

      <Grid.Col span={12}>
        <Group position="apart" m="xl">
          <Title order={4}>
            {t("common.entity.dispatch.profile.info.section.documents.label", "Documents")}
          </Title>
          <HasAccess permissions={["transport_edit"]}>
            <ActionIcon color="green" onClick={supplementalHandlers.open}>
              <IconFilePlus size={16}/>
            </ActionIcon>
          </HasAccess>
        </Group>
        <Divider/>
        <Grid>
          <Grid.Col span={12}>
            <EntityDocumentTable
              objectId={transport.id}/>
          </Grid.Col>
        </Grid>
      </Grid.Col>

      <Grid.Col span={12}>
        <Divider variant="dashed"/>
      </Grid.Col>

      <Grid.Col span={12}>
        <Group position="apart" m="xl">
          <Title order={4}>
            {t("common.entity.dispatch.profile.info.section.images.label", "Images")}
          </Title>
          <Group>
            <ActionIcon onClick={() => downloadObjectPictures(transport.id, undefined)}>
              <IconDownload size={16}/>
            </ActionIcon>
            <HasAccess permissions={["transport_edit"]}>
              <ActionIcon color="green" onClick={supplementalHandlers.open}>
                <IconFilePlus size={16}/>
              </ActionIcon>
            </HasAccess>
          </Group>
        </Group>
        <Divider/>
        <Grid>
          <Grid.Col span={12}>
            <EntityImageCarousel
              m="md"
              objectId={transport.id}
              align={"center"}
              loop={true}
              withIndicators={true}
              styles={{
                indicator: {
                  marginBottom: "8px",
                  backgroundColor: appTheme.fn.lighten(appTheme.fn.primaryColor(), 0.5),
                  '&[data-active]': {
                    backgroundColor: appTheme.fn.primaryColor(),
                  },
                },
              }}/>
          </Grid.Col>
        </Grid>
      </Grid.Col>

      <Grid.Col span={12}>
        <Divider variant="dashed"/>
      </Grid.Col>

      <Grid.Col span={12}>
        <Group position="apart" m="xl">
          <Title order={4}>
            {t("common.entity.dispatch.profile.info.section.comments.label", "Comments")}
          </Title>
          <HasAccess permissions={["transport_edit"]}>
            <ActionIcon color="green" onClick={handlers.open}>
              <IconMessagePlus size={16}/>
            </ActionIcon>
          </HasAccess>
        </Group>
        <Divider/>
        <Grid>
          <Grid.Col span={12}>
            <EntityCommentList objectId={transport.id}/>
          </Grid.Col>
        </Grid>
      </Grid.Col>

      <CommentModal
        opened={opened}
        onClose={handlers.close}
        onSubmit={onCommentSubmit}
        loading={isCommentCreating}/>

      <SupplementalFormModal
        opened={supplementalOpened}
        onClose={supplementalHandlers.close}
        onSubmit={onSupplementalSubmit}
        submitting={isSupplementalCreating}/>
    </Grid>
  );
};

